import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useConfig } from '@browsing/config';
import { removeQueryParam } from '@browsing/utils';

import {
    getSubscriptionCookie,
    setSubscriptionClosedCookie,
    setSubscriptionHiddenCookie,
} from '@client/helpers/subscription-cookie';
import { CONSENT_STATUS, NEWLY_SUBSCRIBED_PARAM } from '@client/redux/modules/newsletter';
import { fetchSubscriptions, fetchConsentStatus } from '@client/redux/modules/newsletter/actions';
import {
    getConsentStatus,
    getSubscriptions,
    getSubscriptionLoading,
    getConsentLoading,
    getNewlySubscribed,
} from '@client/redux/modules/newsletter/selectors';

import { track } from '@client/helpers/tracking';
import { useHidableContainerProps } from '.';

export const WAIT = {
    SUCCESS: 20 * 1000,
    CLOSE: 500,
    HIDE_BEFORE: 500,
};

export const useHidableContainer = ({
    accepted,
    error,
    pending,
    subscriptionTarget,
}: useHidableContainerProps) => {
    const dispatch = useDispatch();
    const config = useConfig();
    const isDoi = config.isEnabled('doi');

    const newlySubscribed = useSelector(getNewlySubscribed);
    const consentStatus = useSelector(getConsentStatus);
    const subscriptions = useSelector(getSubscriptions);
    const subscriptionLoading = useSelector(getSubscriptionLoading);
    const consentLoading = useSelector(getConsentLoading);

    const [hidden, setHidden] = useState(false);
    const [closed, setClosed] = useState(Boolean(getSubscriptionCookie()));
    const [timer, setTimer] = useState<ReturnType<typeof setTimeout>>(null);

    const isSubscribed = useCallback(() => subscriptions.daily, [subscriptions]);
    const hasApprovedConsent = useCallback(
        () => consentStatus === CONSENT_STATUS.APPROVED,
        [consentStatus],
    );

    const hideContainer = useCallback((wait: number) => {
        setTimeout(() => setHidden(true), wait - WAIT.HIDE_BEFORE);

        return setTimeout(() => setClosed(true), wait);
    }, []);

    const isVisible = useCallback(() => {
        const incomplete = isDoi ? !isSubscribed() || !hasApprovedConsent() : !isSubscribed();

        return incomplete && !closed && !subscriptionLoading && !consentLoading && !newlySubscribed;
    }, [
        isDoi,
        isSubscribed,
        hasApprovedConsent,
        closed,
        subscriptionLoading,
        consentLoading,
        newlySubscribed,
    ]);

    const close = useCallback(() => {
        track('secondary_subscription_close_click');
        hideContainer(WAIT.CLOSE);
        setSubscriptionClosedCookie();
    }, [hideContainer]);

    useEffect(() => {
        dispatch(fetchSubscriptions(subscriptionTarget));
        if (isDoi) {
            dispatch(fetchConsentStatus);
        }
    }, [dispatch, isDoi, subscriptionTarget]);

    useEffect(() => {
        if (newlySubscribed) {
            setSubscriptionHiddenCookie();
            removeQueryParam(NEWLY_SUBSCRIBED_PARAM);
        }
    }, [newlySubscribed]);

    useEffect(() => {
        if (accepted && !error && !pending && !timer) {
            const newTimer = hideContainer(WAIT.SUCCESS);
            setTimer(newTimer);
        }
    }, [accepted, error, hideContainer, pending, timer]);

    return {
        hidden,
        close,
        isVisible,
    };
};
